/**
 * T27 — one table for every word this feature says to a human.
 *
 * WHY A TABLE RATHER THAN INLINE STRINGS. The same session is described on five
 * surfaces — the staff bar, the customer bar, the consent modal, the Access Log
 * and the admin console — and each was written at a different time. Left inline,
 * they drift: one says "impersonation", another "support session", a third "view
 * as". A customer deciding whether to let a vendor into their books is reading
 * for exactly those inconsistencies, and finding them is what makes the feature
 * feel improvised.
 *
 * 🔑 THE WORD "IMPERSONATION" APPEARS NOWHERE, and that is deliberate. It
 * describes the mechanism, not the product, and it is the word that ends up in
 * an aria-label or a customer email by accident. Externally this is SUPPORT
 * ACCESS; internally the two identities are the ACTOR and the SUBJECT.
 *
 * 🔑 PLAIN LANGUAGE, not product vocabulary. The reader is an accountant, not an
 * engineer: "grant", "actor", "subject" and "revoke" are internal terms and do
 * not belong on screen.
 */
export const supportAccessCopy = {
	/** What the thing is called, everywhere. */
	featureName: "Support access",

	staffBar: {
		acting:
			"You are viewing this account as another user. Read-only — you cannot make changes.",
		/**
		 * The write-mode wording.
		 *
		 * 🚨 IT MUST NOT READ AS A MILDER VERSION OF THE READ-ONLY LINE. The
		 * read-only bar's real function is reassurance — the operator glances at
		 * it and knows nothing they click can do damage. In write mode that
		 * reassurance is false, and a bar that still looks like the familiar one
		 * is worse than no bar: it is the safety mechanism actively lying. So the
		 * sentence leads with the capability, not with whose account it is.
		 */
		actingWrite:
			"You can CHANGE this account. Everything you do is recorded against you and applied to the customer's real data.",
		endSession: "End session",
		expiryTooltip: "Ends automatically at the time the customer approved.",
	},

	/** The alarm state. Loud on purpose — see DZ2. */
	mismatch: {
		title:
			"This tab thinks it is in a support session. The server says it is not.",
		consequence:
			"Anything you do here is happening as YOU, not as the customer. Stop and end the session.",
		unconfirmed: "Cannot confirm whether this is a support session.",
		unconfirmedConsequence: "Do not act until this resolves.",
		clear: "Clear session",
	},

	customer: {
		liveTitle: "Equility support is viewing this account.",
		liveDetail: "Read-only — they cannot make changes.",
		/**
		 * 🔑 THE CUSTOMER IS TOLD THE TRUTH ABOUT WRITE MODE. A write session is
		 * only reachable from the override tier, which is never announced to the
		 * customer in advance — but the moment they are looking at this bar they
		 * have already discovered the session, and telling them "read-only" at
		 * that point would be a plain false statement about their own books.
		 */
		/*
		 * 🪤 This used to end "Every change is recorded in your access log." The
		 * customer-facing Access Log was removed in this release — the ledger is
		 * now a staff-only surface at `/admin/access-log`, and the company-scoped
		 * routes keep no history endpoint — so that sentence pointed the customer
		 * at a page they can no longer open. Per the note above, this block exists
		 * to tell the customer the truth, and a promise of a record they cannot
		 * see is exactly the kind of false statement it is guarding against.
		 * Removed rather than reworded: do not promise a record without a surface
		 * the customer can actually reach.
		 */
		liveDetailWrite: "They can make changes to this account.",
		endAccess: "End access",
		endConfirm: "End Equility's access now?",
		endConfirmDetail: "Support will lose access to this account immediately.",
		/*
		 * NO "could not check" LINE HERE ON PURPOSE. The customer bar renders
		 * only a KNOWN live session; a failed or still-loading lookup renders
		 * nothing. See `CustomerSupportAccessBar`. The staff bar keeps its
		 * `unconfirmed` wording above, where the tab is holding an act-as id and
		 * the ambiguity is real.
		 */
	},

	consent: {
		title: "Equility support is asking to access this account",
		intro:
			"A member of Equility support has asked to view this account as one of your users, to help with a support request.",
		reasonLabel: "Their reason:",
		ticketLabel: "Ticket:",
		scopeTitle: "This access is read-only and time-limited.",
		/** Minutes is interpolated — the cap is a server decision, not a UI one. */
		scopeDetail: (minutes: number) =>
			`If you allow it, support can view this account for up to ${minutes} minutes and cannot change anything. You can end the session at any time.`,
		allow: "Allow access",
		deny: "Deny",
		defer: "Decide later",
	},

	/**
	 * The Access Log, now an ADMIN surface at `/admin/access-log` rather than a
	 * customer-facing company-settings tab. The voice changed with the audience:
	 * the reader is Equility staff auditing privileged access across every
	 * customer, so nothing here addresses the customer as "you".
	 */
	accessLog: {
		title: "Access log",
		subtitle:
			"Every recorded action Equility staff took while acting for a customer, across all companies.",
		/** The most-read state on the screen, and a reassuring one. */
		empty: "No Equility staff access has been recorded.",
		/** A filtered empty result says nothing about the ledger as a whole. */
		emptyFiltered: "No event matches these filters.",
		emptyFilteredDetail: "Clear them to see the whole ledger.",
		/**
		 * Must never be confusable with the empty state. "Nothing was recorded"
		 * is a reassuring claim about an audit ledger; showing it because the
		 * request failed would be a lie told on the one screen whose whole job is
		 * to be believed.
		 */
		error: "Could not load the access log",
		errorDetail:
			"This does not mean nothing happened — we could not check. Please retry.",
	},

	/**
	 * Ledger event types, in plain language. The audience is a reviewer reading
	 * a security history, not an engineer reading a log.
	 *
	 * 🔑 NO SECOND PERSON. These read "Customer approved", not "You approved":
	 * the only surface that renders them is the staff Access Log, where "you"
	 * would name the wrong party in the one place that has to be exact about who
	 * did what.
	 */
	event: {
		"request.created": "Access requested",
		"request.approved": "Customer approved access",
		"request.denied": "Customer denied access",
		"request.break_glass_created": "Emergency access requested",
		"grant.issued": "Session started",
		"grant.break_glass_approved": "Emergency access approved by Equility",
		"grant.ended": "Session ended",
		"grant.revoked": "Access revoked",
		"grant.expired": "Session expired",
		"grant.owner_mismatch": "Access refused",
		"grant.subject_is_admin": "Access refused",
		"grant.subject_not_in_company": "Access refused",
		"grant.subject_missing": "Access refused",
	} as Record<string, string>,

	breakGlass: {
		title: "Break-glass access (no customer consent)",
		warning:
			"This bypasses the customer's consent. Use it only when they cannot be asked.",
		requestButton: "Request break-glass",
		approveButton: "Approve as second staff",
		awaitingColleague: "Awaiting a colleague",
		exhausted: "Your break-glass allowance is used up.",
		exhaustedDetail:
			"Escalate instead of bypassing again. The limit resets on a rolling 7-day window.",
	},
} as const;

/** Plain-language label for a ledger event, falling back to the raw key. */
export function describeSupportAccessEvent(eventType: string): string {
	return supportAccessCopy.event[eventType] ?? eventType;
}
